<template>
  <Transition
    enter-active-class="ease-out duration-200"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="ease-in duration-150"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="isOpen"
      class="fixed inset-0 z-50 bg-white bg-opacity-75 flex items-center justify-center p-4"
      @click.self="emit('close')"
    >
      <div class="w-full max-w-6xl rounded-2xl border border-zinc-200 bg-white shadow-[0_10px_60px_rgba(0,0,0,0.20)] overflow-hidden">
        <div class="relative h-14 border-b border-zinc-200 flex items-center justify-center">
          <h3 class="text-[18px] font-semibold text-zinc-900">
            Viewers for Post #{{ postId || '-' }}
          </h3>

          <button
            type="button"
            class="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center"
            aria-label="Close"
            @click="emit('close')"
          >
            <XMarkIcon class="w-6 h-6 text-zinc-700" />
          </button>
        </div>

        <div class="max-h-[70vh] overflow-y-auto app-scroll">
          <div v-if="isLoading" class="px-4 py-8 text-sm text-zinc-500">
            Loading viewers...
          </div>

          <div v-else-if="error" class="px-4 py-8 text-sm text-red-600">
            {{ error }}
          </div>

          <div v-else-if="!normalizedRows.length" class="px-4 py-8 text-sm text-zinc-500">
            No viewer rows for this post yet.
          </div>

          <div v-else class="overflow-x-auto">
            <table class="min-w-full text-sm">
              <thead class="bg-zinc-50 text-zinc-600 sticky top-0">
                <tr>
                  <th class="text-left px-4 py-2 font-medium">Viewer</th>
                  <th class="text-left px-4 py-2 font-medium">User Type</th>
                  <th class="text-right px-4 py-2 font-medium">Total</th>
                  <th class="text-right px-4 py-2 font-medium">Feed</th>
                  <th class="text-right px-4 py-2 font-medium">Clicked To Open</th>
                  <th class="text-left px-4 py-2 font-medium">First Seen</th>
                  <th class="text-left px-4 py-2 font-medium">Last Seen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="v in normalizedRows" :key="`${v.userType}:${v.userId}`" class="border-t border-zinc-100">
                  <td class="px-4 py-2 text-zinc-800">
                    <div class="flex items-center gap-2">
                      <img
                        :src="v.avatarUrl"
                        alt="Viewer avatar"
                        class="h-7 w-7 rounded-full object-cover border border-zinc-200"
                      />
                      <span class="truncate max-w-[220px]">{{ v.name }}</span>
                    </div>
                  </td>
                  <td class="px-4 py-2 text-zinc-600">{{ v.userType }}</td>
                  <td class="px-4 py-2 text-right text-zinc-800">{{ v.totalViews }}</td>
                  <td class="px-4 py-2 text-right text-zinc-800">{{ v.feedViews }}</td>
                  <td class="px-4 py-2 text-right text-zinc-800">{{ v.slugViews }}</td>
                  <td class="px-4 py-2 text-zinc-600">{{ formatDateTime(v.firstViewedAt) }}</td>
                  <td class="px-4 py-2 text-zinc-600">{{ formatDateTime(v.lastViewedAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { computed, defineEmits, defineProps } from 'vue';
import { XMarkIcon } from '@heroicons/vue/24/outline';

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  postId: { type: [Number, String], default: null },
  isLoading: { type: Boolean, default: false },
  error: { type: String, default: '' },
  rows: { type: Array, default: () => [] },
});

const emit = defineEmits(['close']);

const defaultAvatar = new URL(
  '@/assets/media/images/woman-avatar.jpg',
  import.meta.url
).href;

function formatDateTime(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString();
}

const normalizedRows = computed(() =>
  (Array.isArray(props.rows) ? props.rows : []).map((row) => ({
    ...row,
    avatarUrl: row?.avatarUrl || defaultAvatar,
  }))
);
</script>

<style scoped>

/* ─────────────────────────────────────────────
   Creator layout scrollbar (right column only)
   ─────────────────────────────────────────── */

/* Firefox base */
.app-scroll {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
  /* try to keep layout width stable */
  scrollbar-gutter: stable;
}

/* WebKit base (Chrome / Edge / Safari) */
.app-scroll::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

/* Hide old-school buttons/arrows (where they exist) */
.app-scroll::-webkit-scrollbar-button {
  display: none;
  width: 0;
  height: 0;
  background: transparent;
}

/* Track fully invisible so it looks “on top” of content */
.app-scroll::-webkit-scrollbar-track {
  background: transparent;
}

/* Thumb invisible by default */
.app-scroll::-webkit-scrollbar-thumb {
  background-color: transparent;
  border-radius: 999px;
}

/* On hover over the scroll area: fade thumb in */
.app-scroll:hover {
  scrollbar-color: rgba(145, 150, 156, 0.541) transparent; /* Firefox */
}

.app-scroll:hover::-webkit-scrollbar-thumb {
  background-color: rgba(140, 143, 148, 0.479);
}

/* Slightly stronger when directly hovering the thumb */
.app-scroll:hover::-webkit-scrollbar-thumb:hover {
  background-color: rgba(203, 213, 225, 0.65);
}
</style>